import React, { useState } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { uid } from "../utils";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Sparkles, Loader2, FileText, Plus, Minus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ObjectsWorkArea from "./ObjectsWorkArea";
import PackingSection from "./PackingSection";
import { CatalogPicker } from "./CatalogPicker";

const CONF = {
  high: { label: "Alta", cls: "bg-green-100 text-green-700" },
  medium: { label: "Media", cls: "bg-amber-100 text-amber-700" },
  low: { label: "Bassa", cls: "bg-red-100 text-red-700" },
};

export default function TextWorkArea() {
  const { mode, inputText, setInputText, catalog, resolveEntries, replaceItems, setMode, setRitiro, setClienteNome, setAbitiBox } = useQuote();
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [results, setResults] = useState(null);
  const [detectedCap, setDetectedCap] = useState(null);

  const isDone = mode === "text_done";

  const findCat = (label) => catalog.find((c) => c.label.toLowerCase() === String(label || "").toLowerCase());
  const complete = (r) => r.l && r.w && r.h;

  const analyze = async () => {
    if (!inputText.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/extract-items", { text: inputText });
      // build list, prefill dims: text -> catalog
      let list = data.items.map((it) => {
        const c = findCat(it.catalogMatch) || findCat(it.label);
        const textDims = !!it.dims_from_text && (it.length_cm || it.width_cm || it.height_cm);
        return {
          ...it, id: uid(), qty: it.qty || 1, include: true,
          l: it.length_cm || (c ? c.length_cm : "") || "",
          w: it.width_cm || (c ? c.width_cm : "") || "",
          h: it.height_cm || (c ? c.height_cm : "") || "",
          dimsEstimated: !c && !textDims,
        };
      });
      // AI-fill any still-missing dimensions
      const missing = list.filter((r) => !complete(r));
      if (missing.length) {
        try {
          const { data: est } = await api.post("/ai/estimate-prices", {
            items: missing.map((m) => ({ label: m.label, dims: { l: +m.l || 0, w: +m.w || 0, h: +m.h || 0 } })),
          });
          const byLabel = {};
          (est.estimates || []).forEach((e) => { byLabel[String(e.label).toLowerCase()] = e; });
          list = list.map((r) => {
            if (complete(r)) return r;
            const e = byLabel[String(r.label).toLowerCase()];
            if (!e) return r;
            return { ...r, l: r.l || e.length_cm || "", w: r.w || e.width_cm || "", h: r.h || e.height_cm || "", dimsEstimated: true };
          });
        } catch { /* ignore */ }
      }
      setResults(list);
      if (data.cap) { setDetectedCap(data.cap); setRitiro((r) => ({ ...r, cap: data.cap })); }
      else if (data.address) setDetectedCap(data.address);
      // Build client name: "<name> (form)" (literal "(form)", no phone)
      const nm = (data.clientName || "").trim();
      if (nm) setClienteNome(`${nm} (form)`);
      // Auto-enable the "scatola porta abiti" section if the request mentions clothing
      if (/\b(abiti|abito|vestit|capi|capo|camic|magliett|cappott|piumin|giacc|guardaroba|maglion|felp|pantalon|gonn)/i.test(inputText)) {
        setAbitiBox(true);
      }
      toast.success(`${data.items.length} oggetti estratti`);
    } catch {
      toast.error("Errore durante l'analisi AI");
    } finally {
      setLoading(false);
    }
  };

  const setField = (id, patch) => setResults((rs) => rs.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const add = async () => {
    const chosen = results.filter((r) => r.include);
    if (chosen.length === 0) { toast.error("Seleziona almeno un oggetto"); return; }
    setAdding(true);
    try {
      const built = await resolveEntries(chosen.map((r) => ({
        label: r.label, qty: r.qty, catalogMatch: r.catalogMatch,
        dims: { l: parseFloat(r.l) || 0, w: parseFloat(r.w) || 0, h: parseFloat(r.h) || 0 },
        dimsEstimated: !!r.dimsEstimated,
        dimsFromText: !r.dimsEstimated && parseFloat(r.l) > 0 && parseFloat(r.w) > 0 && parseFloat(r.h) > 0,
      })));
      replaceItems(built);
      setMode("text_done");
      toast.success("Preventivo aggiornato dal testo");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="h-5 w-5 text-[#1a5f5a]" />
          <h3 className="font-display font-semibold text-lg">
            {isDone ? "Testo del cliente" : "Estrai oggetti dal testo"}
          </h3>
        </div>
        <Textarea value={inputText} onChange={(e) => setInputText(e.target.value)} rows={6}
          placeholder="Incolla qui la lista degli oggetti o il messaggio del cliente... (es. 'un divano 3 posti 200x90x85, 5 scatole, zona 20142')"
          data-testid="text-input" />
        <Button onClick={analyze} disabled={loading || !inputText.trim()}
          className="mt-3 bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="text-analyze-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (results ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />)}
          {results ? "Rianalizza con AI" : "Analizza con AI"}
        </Button>
      </div>

      {results && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <h3 className="font-display font-semibold text-lg mb-1">Oggetti rilevati</h3>
          <p className="text-xs text-slate-400 mb-4">Modifica quantità e dimensioni (cm) prima di aggiungere. Le dimensioni indicate nel testo sono precompilate.</p>
          {detectedCap && (
            <div className="mb-4 text-sm bg-[#1a5f5a]/5 border border-[#1a5f5a]/10 rounded-md p-3 text-[#134744]">
              Indirizzo/CAP rilevato: <strong>{detectedCap}</strong> (pre-compilato nel ritiro)
            </div>
          )}
          <div className="space-y-2">
            {results.map((r) => {
              const conf = CONF[r.confidence] || CONF.medium;
              return (
                <div key={r.id} className="border border-slate-200 rounded-md p-3" data-testid={`extracted-item-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Switch checked={r.include} onCheckedChange={(v) => setField(r.id, { include: v })} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-slate-800">{r.label}</div>
                      <CatalogPicker
                        value={r.catalogMatch || null}
                        testid={`extracted-catalog-picker-${r.id}`}
                        onSelect={(c) => c
                          ? setField(r.id, { catalogMatch: c.label, l: c.length_cm, w: c.width_cm, h: c.height_cm, dimsEstimated: false })
                          : setField(r.id, { catalogMatch: null })}
                      />
                    </div>
                    <Badge className={`${conf.cls} text-[10px]`}>{conf.label}</Badge>
                    <div className="flex items-center gap-1.5">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setField(r.id, { qty: Math.max(1, r.qty - 1) })}><Minus className="h-3 w-3" /></Button>
                      <span className="w-5 text-center text-sm">{r.qty}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setField(r.id, { qty: r.qty + 1 })}><Plus className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2 pl-11">
                    {[["l", "Lung."], ["w", "Larg."], ["h", "Alt."]].map(([k, lbl]) => (
                      <div key={k}>
                        <label className="text-[10px] uppercase text-slate-400">{lbl} (cm)</label>
                        <Input type="number" value={r[k]} placeholder="—" onChange={(e) => setField(r.id, { [k]: e.target.value, dimsEstimated: false })}
                          className="h-8" data-testid={`extracted-dim-${k}-${r.id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <Button onClick={add} disabled={adding} className="mt-4 bg-[#1a5f5a] hover:bg-[#134744] gap-2" data-testid="text-add-button">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {isDone ? "Aggiorna preventivo" : "Aggiungi al preventivo"}
          </Button>
        </div>
      )}

      {!isDone && <PackingSection />}

      {isDone && <ObjectsWorkArea />}
    </div>
  );
}
